// 🧢 Кепка сайта src/components/Header.jsx

import React, { useState } from "react";
import styled, { css } from "styled-components";
import { useTheme } from "../context/ThemeContext";
import ThemeToggle from "../context/ThemeToggle";

// --- links ---
const navLinks = [
  { href: "#hero", label: "Главная" },
  { href: "#about", label: "О себе" },
  { href: "#skills", label: "Навыки" },
  { href: "#projects", label: "Проекты" },
  { href: "#contacts", label: "Контакты" },
];

const scrollToSection = (e, id) => {
  e.preventDefault();
  const section = document.querySelector(id);
  if (section) {
    // Закрываем меню и затем плавно скроллим
    setTimeout(() => {
      section.scrollIntoView({ behavior: "smooth" });
    }, 100);
  }
};

const BREAKPOINT = "768px"; // point перехода для мобильных/ПК

const StyledHeader = styled.header`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  position: fixed;
  top: 0;
  z-index: 50;

  background-color: var(--color-header-bg);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--color-header-border);

  font-family: "Philosopher";

  font-size: 1.3rem;
`;

const Logo = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  z-index: 100;
`;

const LogoImg = styled.img.attrs({ className: "no-invert" })`
  width: 60px;
  height: 45px;
  object-fit: contain;
  filter: none !important;

  @media (max-width: 500px) {
    width: 40px;
    height: 35px;
  }
`;

const DesktopNav = styled.nav`
  display: none;
  flex-grow: 1;
  justify-content: center;

  @media (min-width: ${BREAKPOINT}) {
    display: flex;
    gap: 2rem;
  }
`;

const NavLink = styled.a`
  color: var(--color-nav-link);
  text-decoration: none;
  font-weight: 500;
  padding: 0.25rem 0.5rem;
  border-bottom: 2px solid transparent;
  transition: color 0.3s ease, border-color 0.3s ease;

  &:hover {
    color: var(--color-nav-link-hover);
    border-color: var(--color-nav-link-hover);
  }
  &:focus {
    outline: none;
  }
`;

// (Контейнер только для ПК)
const ThemeToggleWrapper = styled.div`
  display: none; /* Скрыт по умолчанию на мобильных */
  z-index: 100;

  @media (min-width: ${BREAKPOINT}) {
    display: block; /* Показать на ПК */
    margin-left: 2rem;
  }
`;

const DesktopControls = styled.div`
  display: none;
  align-items: center;

  @media (min-width: ${BREAKPOINT}) {
    display: flex;
  }
`;

const BurgerButton = styled.button`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  width: 2rem;
  height: 2rem;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  z-index: 100;

  @media (min-width: ${BREAKPOINT}) {
    display: none;
  }

  &:focus {
    outline: none;
  }
`;

const BurgerLine = styled.div`
  width: 2rem;
  height: 0.25rem;
  background: var(--color-burger);
  border-radius: 10px;
  transition: all 0.3s linear;
  position: relative;
  transform-origin: 1px;

  ${(props) =>
    props.$open &&
    css`
      &:first-child {
        transform: rotate(45deg);
      }
      &:nth-child(2) {
        opacity: 0;
        transform: translateX(20px);
      }
      &:last-child {
        transform: rotate(-45deg);
      }
    `}
`;

// (Контейнер только для мобильного меню)
const MobileToggleContainer = styled.div`
  padding: 30px 0 0;
  display: flex;
  justify-content: center;
  width: 100%;

  /* Гарантируем, что этот контейнер виден только на мобильных */
  @media (min-width: ${BREAKPOINT}) {
    display: none;
  }
`;

const MobileMenu = styled.nav`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: var(--color-mobile-menu-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  z-index: 90;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: transform 0.3s ease-in-out;
  transform: translateX(100%);

  ${(props) =>
    props.$open &&
    css`
      transform: translateX(0);
    `}

  @media (min-width: ${BREAKPOINT}) {
    display: none;
  }

  & ${NavLink} {
    font-size: 1.5rem;
    padding: 1.5rem 0;
    color: var(--color-text);
    border-bottom: none;

    &:hover {
      color: var(--color-nav-link-hover);
    }
  }
`;

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleLinkClick = (e, href) => {
    scrollToSection(e, href);
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };
  const { theme } = useTheme();
  const currentLogoSrc =
    theme === "dark" ? "./images/w-logo.png" : "./images/logo.png";
  return (
    <StyledHeader>
      {/* нативная реклама */}
      <Logo>
        {/* <LogoImg src="./images/logo.png" alt="Логотип сайта" /> */}
        <LogoImg src={currentLogoSrc} />
      </Logo>

      {/* 2. Десктопная навигация */}
      <DesktopNav>
        {navLinks.map((link) => (
          <NavLink
            key={link.href}
            href={link.href}
            onClick={(e) => handleLinkClick(e, link.href)}
          >
            {link.label}
          </NavLink>
        ))}
      </DesktopNav>

      {/* Контролы для ПК (ThemeToggle) */}
      <DesktopControls>
        <ThemeToggleWrapper>
          <ThemeToggle />
        </ThemeToggleWrapper>
      </DesktopControls>

      {/* Кнопка Бургера (только для мобильных) */}
      <BurgerButton
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        aria-label="Переключить навигационное меню"
      >
        <BurgerLine $open={isMenuOpen} />
        <BurgerLine $open={isMenuOpen} />
        <BurgerLine $open={isMenuOpen} />
      </BurgerButton>

      {/* Мобильное меню (открывается/закрывается) */}
      <MobileMenu $open={isMenuOpen}>
        {navLinks.map((link) => (
          <NavLink
            key={link.href}
            href={link.href}
            onClick={(e) => handleLinkClick(e, link.href)}
          >
            {link.label}
          </NavLink>
        ))}

        <MobileToggleContainer>
          <ThemeToggle $isMobileMenu={true} />
        </MobileToggleContainer>
      </MobileMenu>
    </StyledHeader>
  );
}
